import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as d3 from 'd3';
import { Box, IconButton, Typography, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';

// Generation colors - LIGHT pastel colors for descendants
const GENERATION_COLORS = {
  root: '#00FFFF',
  spouse: '#FFB6C1',
  child: '#98FB98',
  grandchild: '#FFFACD',
};

// Depth-based colors for deeper generations
const DEPTH_COLORS = [
  '#00FFFF',  // 0 - Cyan
  '#FFB6C1',  // 1 - Light Pink
  '#98FB98',  // 2 - Pale Green
  '#FFFACD',  // 3 - Lemon Chiffon
  '#FFE4B5',  // 4 - Moccasin
  '#E6E6FA',  // 5 - Lavender
  '#B0E0E6',  // 6 - Powder Blue
  '#FFDAB9',  // 7 - Peach Puff
  '#D8BFD8',  // 8 - Thistle
  '#F0FFF0',  // 9 - Honeydew
  '#FFF0F5',  // 10 - Lavender Blush
  '#F5FFFA',  // 11 - Mint Cream
  '#87CEEB',  // 12 - Sky Blue
  '#F0E68C',  // 13 - Khaki
  '#DDA0DD',  // 14 - Plum
  '#90EE90',  // 15 - Light Green
  '#FFD700',  // 16 - Gold
  '#FF7F50',  // 17 - Coral
  '#00CED1',  // 18 - Dark Turquoise
  '#FF69B4',  // 19 - Hot Pink
];

// Cloudinary face-centered thumbnail: small, fast, cropped to the face
const photoThumb = (url, size = 80) =>
  url && typeof url === 'string' && url.includes('/upload/')
    ? url.replace('/upload/', `/upload/w_${size},h_${size},c_fill,g_face/`)
    : url;


const ANCESTOR_COLOR = '#A0A0A0';
const FOUNDER_COLOR = '#00FFFF';

// Sort children by birthOrder (lower numbers first, undefined last)
const sortByBirthOrder = (children) => {
  if (!children || !Array.isArray(children)) return [];
  return [...children].sort((a, b) => {
    const orderA = a.birthOrder ?? 9999;
    const orderB = b.birthOrder ?? 9999;
    if (orderA !== orderB) return orderA - orderB;
    // If same birthOrder, sort by name as fallback
    return (a.name || '').localeCompare(b.name || '');
  });
};

const ANCESTORS = [
  'Bantu People', 'Kamba Tribe', 'Ithanga Clan', 'Aumon Clan',
  'Kitavi Seleitan', 'Munyanyaki', 'Kakaa Munyanyeki',
  "Muny'athika Kakii", "Ndingi Muny'athika", 'Kithue Ndingi',
  'Mwanango Kithue', 'Kitavi Mwanango'
];

const getNodeColor = (type, depth, name) => {
  if (name && (name === 'Mwendwa Kitavi' || name === 'Chief Mwendwa Kitavi' || name.includes('Mwendwa Kitavi'))) {
    return FOUNDER_COLOR;
  }
  if (name && ANCESTORS.some(ancestor => name.includes(ancestor))) {
    return ANCESTOR_COLOR;
  }
  if (type === 'spouse') return GENERATION_COLORS.spouse;
  
  const colorIndex = Math.min(Math.max(depth, 0), DEPTH_COLORS.length - 1);
  return DEPTH_COLORS[colorIndex];
};

const wrapText = (text, maxWidth = 120) => {
  if (!text || typeof text !== 'string') {
    return { lines: ['Unknown'], lineHeight: 14 };
  }
  const words = text.split(/\s+/);
  const lines = [];
  let currentLine = '';
  
  words.forEach(word => {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length * 7 > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  });
  if (currentLine) lines.push(currentLine);
  
  return { lines: lines.length > 0 ? lines : [text], lineHeight: 14 };
};

const FlowChartView = ({ data, onPersonClick, onBack, selectedPerson }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isReady, setIsReady] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  
  const [visibleNodes, setVisibleNodes] = useState(new Map());
  const [visibleLinks, setVisibleLinks] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [personMap, setPersonMap] = useState({});
  const [lastClickedNodeId, setLastClickedNodeId] = useState(null);
  const currentZoomRef = useRef(null);

  const buildPersonMap = useCallback((rawData) => {
    const map = {};
    
    // DEBUG: Check if birthOrder exists in raw data
    const peopleWithBirthOrder = rawData.filter(p => p.birthOrder);
    console.log('=== BIRTH ORDER DEBUG ===');
    console.log('People with birthOrder in raw data:', peopleWithBirthOrder.map(p => ({
      name: p.name,
      birthOrder: p.birthOrder
    })));
    
    rawData.forEach((person) => {
      if (person && person._id) {
        map[person._id] = {
          ...person,
          children: [],
          spouses: [],
          kawetos: [],
        };
      }
    });

    rawData.forEach((person) => {
      if (!person || !person._id) return;

      // Build spouse relationships
      if (person.spouses && Array.isArray(person.spouses)) {
        person.spouses.forEach((spouse) => {
          const spouseId = spouse?._id || spouse;
          if (map[spouseId]) {
            if (map[spouseId].role === 'KAWETO') {
              if (!map[person._id].kawetos.find(k => k._id === spouseId)) {
                map[person._id].kawetos.push(map[spouseId]);
              }
            } else {
              if (!map[person._id].spouses.find(s => s._id === spouseId)) {
                map[person._id].spouses.push(map[spouseId]);
              }
            }
          }
        });
      }

      // Build kaweto relationships
      if (person.kawetos && Array.isArray(person.kawetos)) {
        person.kawetos.forEach((kaweto) => {
          const kawetoId = kaweto?._id || kaweto;
          if (map[kawetoId] && !map[person._id].kawetos.find(k => k._id === kawetoId)) {
            map[person._id].kawetos.push(map[kawetoId]);
          }
        });
      }

      // Build children from person.children array
      if (person.children && Array.isArray(person.children)) {
        person.children.forEach((child) => {
          const childId = child?._id || child;
          if (map[childId] && !map[person._id].children.find(c => c._id === childId)) {
            map[person._id].children.push(map[childId]);
          }
        });
      }

      // ALSO build children from mother/father fields (reverse relationship)
      // If this person has a mother, add this person as child of the mother
      // BUT: if this person is a KAWETO, add to kawetos array instead
      if (person.mother) {
        const motherId = person.mother?._id || person.mother;
        if (map[motherId]) {
          if (person.role === 'KAWETO') {
            // Kawetos go in kawetos array, not children
            if (!map[motherId].kawetos.find(k => k._id === person._id)) {
              map[motherId].kawetos.push(map[person._id]);
            }
          } else if (!map[motherId].children.find(c => c._id === person._id)) {
            map[motherId].children.push(map[person._id]);
          }
        }
      }

      // If this person has a father, add this person as child of the father
      if (person.father) {
        const fatherId = person.father?._id || person.father;
        if (map[fatherId]) {
          if (person.role === 'KAWETO') {
            // Kawetos go in kawetos array, not children
            if (!map[fatherId].kawetos.find(k => k._id === person._id)) {
              map[fatherId].kawetos.push(map[person._id]);
            }
          } else if (!map[fatherId].children.find(c => c._id === person._id)) {
            map[fatherId].children.push(map[person._id]);
          }
        }
      }

      // If this person has a socialParent (for kaweto relationships)
      // Add as kaweto, not as child
      if (person.socialParent) {
        const socialParentId = person.socialParent?._id || person.socialParent;
        if (map[socialParentId]) {
          if (person.role === 'KAWETO') {
            // The socialParent should have this kaweto in their kawetos array
            if (!map[socialParentId].kawetos.find(k => k._id === person._id)) {
              map[socialParentId].kawetos.push(map[person._id]);
            }
          } else {
            // Non-kaweto with socialParent goes to children
            if (!map[socialParentId].children.find(c => c._id === person._id)) {
              map[socialParentId].children.push(map[person._id]);
            }
          }
        }
      }
    });

    // Sort all children arrays by birthOrder for consistent display
    Object.values(map).forEach(person => {
      if (person.children && person.children.length > 0) {
        person.children = sortByBirthOrder(person.children);
      }
    });

    return map;
  }, []);

  // Initialize tree
  useEffect(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return;

    const map = buildPersonMap(data);
    setPersonMap(map);
    
    let rootPerson;
    if (selectedPerson && map[selectedPerson._id]) {
      rootPerson = map[selectedPerson._id];
    } else {
      rootPerson = data.find((p) => p.name === 'Mwendwa Kitavi' && !p.mother && !p.father) || data[0];
      if (rootPerson) rootPerson = map[rootPerson._id];
    }

    if (!rootPerson) return;

    const initialNodes = new Map();
    const hasRelations = (rootPerson.children?.length > 0) || 
                         (rootPerson.spouses?.length > 0) || 
                         (rootPerson.kawetos?.length > 0);
    
    initialNodes.set(rootPerson._id, {
      id: rootPerson._id,
      name: rootPerson.name,
      type: 'root',
      depth: 0,
      hasChildren: hasRelations,
    });

    setVisibleNodes(initialNodes);
    setVisibleLinks([]);
    setExpandedNodes(new Set());
    setLastClickedNodeId(null);
    currentZoomRef.current = null;
  }, [data, selectedPerson, buildPersonMap]);

  // Helper function to collapse a node and all its descendants
  const collapseNodeAndDescendants = useCallback((nodeId, currentNodes, currentLinks, currentExpanded) => {
    const toRemove = new Set();
    const queue = [];
    
    currentLinks.forEach(link => {
      if (link.source === nodeId) {
        queue.push(link.target);
      }
    });

    while (queue.length > 0) {
      const id = queue.shift();
      if (toRemove.has(id)) continue;
      toRemove.add(id);
      
      currentLinks.forEach(link => {
        if (link.source === id && !toRemove.has(link.target)) {
          queue.push(link.target);
        }
      });
    }

    const newNodes = new Map();
    currentNodes.forEach((node, id) => {
      if (!toRemove.has(id)) {
        newNodes.set(id, node);
      }
    });

    const newLinks = currentLinks.filter(link => 
      !toRemove.has(link.source) && !toRemove.has(link.target) && link.source !== nodeId
    );

    const newExpanded = new Set(currentExpanded);
    newExpanded.delete(nodeId);
    toRemove.forEach(id => newExpanded.delete(id));

    return { newNodes, newLinks, newExpanded };
  }, []);

  // Expand node - collapses siblings at the same level first
  const expandNode = useCallback((nodeId) => {
    if (expandedNodes.has(nodeId)) return;

    const person = personMap[nodeId];
    if (!person) return;

    const parentNode = visibleNodes.get(nodeId);
    if (!parentNode) return;

    // First, collapse any siblings/related nodes that are expanded
    let currentNodes = new Map(visibleNodes);
    let currentLinks = [...visibleLinks];
    let currentExpanded = new Set(expandedNodes);

    // Find nodes to collapse:
    // 1. Siblings at the same depth with the same parent
    // 2. If clicking a kaweto, also collapse children of the same parent (wife's children)
    // 3. If clicking a child, also collapse kawetos of the same parent
    const nodesToCollapse = [];
    currentNodes.forEach((node, id) => {
      if (id !== nodeId && node.parentId === parentNode.parentId && currentExpanded.has(id)) {
        // Same parent - collapse this node
        nodesToCollapse.push(id);
      }
    });

    // Collapse each related node
    nodesToCollapse.forEach(collapseId => {
      const result = collapseNodeAndDescendants(collapseId, currentNodes, currentLinks, currentExpanded);
      currentNodes = result.newNodes;
      currentLinks = result.newLinks;
      currentExpanded = result.newExpanded;
    });

    // Now expand the clicked node
    const newNodes = new Map(currentNodes);
    const newLinks = [...currentLinks];
    const newExpanded = new Set(currentExpanded);
    newExpanded.add(nodeId);

    const spouseCount = (person.spouses?.length || 0);
    const kawetoCount = (person.kawetos?.length || 0);
    
    // ALWAYS handle kawetos first - they ALWAYS go to the side with branch arrow
    // This applies to both wives (like Ng'amumo) and men who might have kawetos
    if (kawetoCount > 0) {
      person.kawetos?.forEach((kaweto) => {
        if (!newNodes.has(kaweto._id)) {
          const hasRelations = (kaweto.children?.length > 0) || 
                               (kaweto.spouses?.length > 0) || 
                               (kaweto.kawetos?.length > 0);
          newNodes.set(kaweto._id, {
            id: kaweto._id,
            name: kaweto.name,
            type: 'kaweto',
            depth: parentNode.depth, // Same depth - positioned at branch level
            parentId: nodeId,
            hasChildren: hasRelations,
            isSideSpouse: true,
            isKawetoOfWife: true,
          });
          newLinks.push({
            source: nodeId,
            target: kaweto._id,
            type: 'kaweto', // ALWAYS kaweto type for branch arrow
          });
        }
      });
    }

    // Now handle spouses and children based on spouse count
    // CASE 1: Multiple SPOUSES (not counting kawetos) - spouses appear BELOW
    if (spouseCount > 1) {
      person.spouses?.forEach((spouse) => {
        if (!newNodes.has(spouse._id)) {
          const hasRelations = (spouse.children?.length > 0) || 
                               (spouse.spouses?.length > 0) || 
                               (spouse.kawetos?.length > 0);
          newNodes.set(spouse._id, {
            id: spouse._id,
            name: spouse.name,
            type: 'spouse',
            depth: parentNode.depth + 1,
            parentId: nodeId,
            hasChildren: hasRelations,
          });
          newLinks.push({
            source: nodeId,
            target: spouse._id,
            type: 'parent-child',
          });
        }
      });
      // Children NOT shown for men with multiple wives - click on wife to see her children
    }
    // CASE 2: SINGLE spouse - spouse on SIDE, children below
    else if (spouseCount === 1) {
      const singleSpouse = person.spouses[0];
      if (singleSpouse && !newNodes.has(singleSpouse._id)) {
        const hasRelations = (singleSpouse.children?.length > 0) || 
                             (singleSpouse.spouses?.length > 0) || 
                             (singleSpouse.kawetos?.length > 0);
        newNodes.set(singleSpouse._id, {
          id: singleSpouse._id,
          name: singleSpouse.name,
          type: 'spouse',
          depth: parentNode.depth,
          parentId: nodeId,
          hasChildren: hasRelations,
          isSideSpouse: true,
        });
        newLinks.push({
          source: nodeId,
          target: singleSpouse._id,
          type: 'spouse',
        });
      }

      // Children go below - SORTED BY BIRTH ORDER
      sortByBirthOrder(person.children).forEach((child) => {
        // Skip if child is actually a kaweto (already added above)
        if (child.role === 'KAWETO') return;
        if (!newNodes.has(child._id)) {
          const hasRelations = (child.children?.length > 0) || 
                               (child.spouses?.length > 0) || 
                               (child.kawetos?.length > 0);
          newNodes.set(child._id, {
            id: child._id,
            name: child.name,
            type: 'child',
            depth: parentNode.depth + 1,
            parentId: nodeId,
            hasChildren: hasRelations,
          });
          newLinks.push({
            source: nodeId,
            target: child._id,
            type: 'parent-child',
          });
        }
      });
    }
    // CASE 3: NO spouses - just show children below
    else {
      sortByBirthOrder(person.children).forEach((child) => {
        // Skip if child is actually a kaweto (already added above)
        if (child.role === 'KAWETO') return;
        if (!newNodes.has(child._id)) {
          const hasRelations = (child.children?.length > 0) || 
                               (child.spouses?.length > 0) || 
                               (child.kawetos?.length > 0);
          newNodes.set(child._id, {
            id: child._id,
            name: child.name,
            type: 'child',
            depth: parentNode.depth + 1,
            parentId: nodeId,
            hasChildren: hasRelations,
          });
          newLinks.push({
            source: nodeId,
            target: child._id,
            type: 'parent-child',
          });
        }
      });
    }

    setVisibleNodes(newNodes);
    setVisibleLinks(newLinks);
    setExpandedNodes(newExpanded);
    setLastClickedNodeId(nodeId);
  }, [visibleNodes, visibleLinks, expandedNodes, personMap, collapseNodeAndDescendants]);

  // Collapse node
  const collapseNode = useCallback((nodeId) => {
    if (!expandedNodes.has(nodeId)) return;

    const toRemove = new Set();
    const queue = [];
    
    visibleLinks.forEach(link => {
      if (link.source === nodeId) {
        queue.push(link.target);
      }
    });

    while (queue.length > 0) {
      const id = queue.shift();
      if (toRemove.has(id)) continue;
      toRemove.add(id);
      
      visibleLinks.forEach(link => {
        if (link.source === id && !toRemove.has(link.target)) {
          queue.push(link.target);
        }
      });
    }

    const newNodes = new Map();
    visibleNodes.forEach((node, id) => {
      if (!toRemove.has(id)) {
        newNodes.set(id, node);
      }
    });

    const newLinks = visibleLinks.filter(link => 
      !toRemove.has(link.source) && !toRemove.has(link.target) && link.source !== nodeId
    );

    const newExpanded = new Set(expandedNodes);
    newExpanded.delete(nodeId);
    toRemove.forEach(id => newExpanded.delete(id));

    setVisibleNodes(newNodes);
    setVisibleLinks(newLinks);
    setExpandedNodes(newExpanded);
    setLastClickedNodeId(nodeId);
  }, [visibleNodes, visibleLinks, expandedNodes]);

  // Reset tree
  const resetTree = useCallback(() => {
    if (!data || data.length === 0) return;
    
    let rootPerson;
    if (selectedPerson && personMap[selectedPerson._id]) {
      rootPerson = personMap[selectedPerson._id];
    } else {
      rootPerson = data.find((p) => p.name === 'Mwendwa Kitavi') || data[0];
      if (rootPerson && personMap[rootPerson._id]) {
        rootPerson = personMap[rootPerson._id];
      }
    }

    if (!rootPerson) return;

    const initialNodes = new Map();
    const hasRelations = (rootPerson.children?.length > 0) || 
                         (rootPerson.spouses?.length > 0) || 
                         (rootPerson.kawetos?.length > 0);
    
    initialNodes.set(rootPerson._id, {
      id: rootPerson._id,
      name: rootPerson.name,
      type: 'root',
      depth: 0,
      hasChildren: hasRelations,
    });

    setVisibleNodes(initialNodes);
    setVisibleLinks([]);
    setExpandedNodes(new Set());
    setLastClickedNodeId(null);
    currentZoomRef.current = null;
  }, [data, selectedPerson, personMap]);

  // Handle dimensions
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const { width, height } = container.getBoundingClientRect();
      if (width > 0 && height > 0) {
        setDimensions({ width, height });
        setIsReady(true);
      }
      setIsMobile(window.innerWidth <= 600);
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Render tree
  useEffect(() => {
    if (!isReady || dimensions.width <= 0 || dimensions.height <= 0) return;
    if (visibleNodes.size === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 80, right: 40, bottom: 60, left: 40 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const nodeWidth = isMobile ? 100 : 140;
    const nodeHeight = isMobile ? 50 : 60;

    // Photo lookup from the raw people data
    const photoById = {};
    (data || []).forEach((p) => {
      if (p && p._id && p.photoPath) photoById[p._id] = p.photoPath;
    });
    const photoR = isMobile ? 12 : 15;
    const horizontalSpacing = isMobile ? 130 : 180;
    const verticalSpacing = isMobile ? 100 : 120;

    svg
      .attr('width', dimensions.width)
      .attr('height', dimensions.height)
      .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`);

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const nodes = Array.from(visibleNodes.values());
    const links = visibleLinks;

    // Build tree structure for positioning
    // Separate kaweto nodes from regular children
    const nodesByDepth = new Map();
    const childrenByParent = new Map();
    const kawetoByParent = new Map(); // Separate map for kaweto nodes

    nodes.forEach(node => {
      const depth = node.depth || 0;
      if (!nodesByDepth.has(depth)) {
        nodesByDepth.set(depth, []);
      }
      nodesByDepth.get(depth).push(node);

      if (node.parentId) {
        // Separate kaweto from regular children
        if (node.isKawetoOfWife || (node.type === 'kaweto' && node.isSideSpouse)) {
          if (!kawetoByParent.has(node.parentId)) {
            kawetoByParent.set(node.parentId, []);
          }
          kawetoByParent.get(node.parentId).push(node);
        } else {
          if (!childrenByParent.has(node.parentId)) {
            childrenByParent.set(node.parentId, []);
          }
          childrenByParent.get(node.parentId).push(node);
        }
      }
    });

    // Position nodes - center children under their parent
    // Only nodes with kawetos get extended vertical spacing
    
    const positionSubtree = (nodeId, centerX) => {
      const node = visibleNodes.get(nodeId);
      if (!node) return centerX;

      const children = childrenByParent.get(nodeId) || [];
      const kawetos = kawetoByParent.get(nodeId) || [];
      
      // Filter regular children (not side spouses) and SORT BY BIRTH ORDER
      const verticalChildren = children
        .filter(c => !c.isSideSpouse)
        .sort((a, b) => {
          // Get birthOrder from the original person data in personMap
          const personA = personMap[a.id];
          const personB = personMap[b.id];
          const orderA = personA?.birthOrder ?? 9999;
          const orderB = personB?.birthOrder ?? 9999;
          if (orderA !== orderB) return orderA - orderB;
          // Fallback to name comparison
          return (a.name || '').localeCompare(b.name || '');
        });
      
      // Debug: Log children order (can be removed after testing)
      if (verticalChildren.length > 1) {
        console.log(`=== POSITIONING Children of ${node.name} ===`);
        console.log('Before sort order:', children.filter(c => !c.isSideSpouse).map(c => c.name));
        console.log('After sort with birthOrder:', verticalChildren.map(c => ({
          name: c.name,
          birthOrder: personMap[c.id]?.birthOrder ?? 'NOT SET',
          personMapExists: !!personMap[c.id]
        })));
      }
      
      const sideSpouses = children.filter(c => c.isSideSpouse && c.type === 'spouse');

      // Position regular side spouses (same row as parent)
      sideSpouses.forEach((spouse, idx) => {
        spouse.x = centerX + (idx + 1) * (nodeWidth + 30);
        spouse.y = node.y;
      });

      // Calculate Y positions
      // If this node has kawetos, add extra space for the kaweto branch
      const hasKawetos = kawetos.length > 0;
      const extraKawetoSpace = hasKawetos ? verticalSpacing : 0; // 1x extra (total 2x normal)
      
      // parentBottom is where the vertical line starts
      const parentBottom = node.y + nodeHeight / 2;
      // midY is where the horizontal bar is
      const midY = parentBottom + (verticalSpacing - nodeHeight) / 2 + extraKawetoSpace / 2;
      // childrenY is where children nodes are positioned
      const childrenY = node.y + verticalSpacing + extraKawetoSpace;
      
      // Kaweto is positioned at the CENTER of the vertical line
      const kawetoY = parentBottom + (midY - parentBottom) / 2;

      if (verticalChildren.length === 0) {
        node.x = centerX;
        
        // Position kaweto to the right
        kawetos.forEach((kaweto, idx) => {
          kaweto.x = centerX + (idx + 1) * (nodeWidth + 100);
          kaweto.y = kawetoY;
        });
        
        return centerX;
      }

      // Calculate total width needed for vertical children
      const totalWidth = verticalChildren.length * horizontalSpacing;
      let startX = centerX - totalWidth / 2 + horizontalSpacing / 2;

      // Position each vertical child
      verticalChildren.forEach((child, index) => {
        const childX = startX + index * horizontalSpacing;
        // Set child's Y position (with extra space if parent has kawetos)
        child.y = childrenY;
        // Recursively position child's subtree
        positionSubtree(child.id, childX);
      });

      // Center parent over its vertical children
      if (verticalChildren.length > 0) {
        const leftChild = verticalChildren[0];
        const rightChild = verticalChildren[verticalChildren.length - 1];
        node.x = (leftChild.x + rightChild.x) / 2;
        
        // Position kaweto to the right of the rightmost child
        kawetos.forEach((kaweto, idx) => {
          kaweto.x = rightChild.x + (idx + 1) * (nodeWidth + 100);
          kaweto.y = kawetoY;
          // Recursively position kaweto's subtree if expanded
          if (expandedNodes.has(kaweto.id)) {
            positionSubtree(kaweto.id, kaweto.x);
          }
        });
      } else {
        node.x = centerX;
      }

      return node.x;
    };

    // First pass: set Y positions for all nodes based on depth
    nodes.forEach(node => {
      if (node.y === undefined) {
        node.y = node.depth * verticalSpacing;
      }
    });

    // Find root node and position tree
    const rootNode = nodes.find(n => n.type === 'root') || nodes[0];
    if (rootNode) {
      positionSubtree(rootNode.id, width / 2);
    }

    // Draw links with centered connectors
    const linkGroup = g.append('g').attr('class', 'links');

    // Add arrow marker definition for kaweto links
    svg.append('defs').append('marker')
      .attr('id', 'arrowhead')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#333');

    // Draw vertical links (parent to children) - centered tree style
    const verticalLinks = links.filter(l => l.type === 'parent-child');
    
    // Group children by parent for centered connector
    const parentGroups = new Map();
    verticalLinks.forEach(link => {
      if (!parentGroups.has(link.source)) {
        parentGroups.set(link.source, []);
      }
      parentGroups.get(link.source).push(link.target);
    });

    parentGroups.forEach((childIds, parentId) => {
      const parent = visibleNodes.get(parentId);
      if (!parent) return;

      const children = childIds.map(id => visibleNodes.get(id)).filter(Boolean);
      if (children.length === 0) return;

      // Calculate midY - horizontal bar is at fixed (normal) distance above children
      const childTop = Math.min(...children.map(c => c.y)) - nodeHeight / 2;
      // Place horizontal bar at normal spacing above children
      const midY = childTop - (verticalSpacing - nodeHeight) / 2;

      // Draw vertical line from parent to horizontal bar (this can be long for kaweto nodes)
      linkGroup.append('path')
        .attr('d', `M${parent.x},${parent.y + nodeHeight/2} L${parent.x},${midY}`)
        .attr('fill', 'none')
        .attr('stroke', '#66CDAA')
        .attr('stroke-width', 2);

      if (children.length > 1) {
        // Draw horizontal line connecting all children
        const leftX = Math.min(...children.map(c => c.x));
        const rightX = Math.max(...children.map(c => c.x));
        
        linkGroup.append('path')
          .attr('d', `M${leftX},${midY} L${rightX},${midY}`)
          .attr('fill', 'none')
          .attr('stroke', '#66CDAA')
          .attr('stroke-width', 2);
      }

      // Draw vertical lines from horizontal bar to each child (always short/normal)
      children.forEach(child => {
        linkGroup.append('path')
          .attr('d', `M${child.x},${midY} L${child.x},${child.y - nodeHeight/2}`)
          .attr('fill', 'none')
          .attr('stroke', '#66CDAA')
          .attr('stroke-width', 2);
      });
    });

    // Draw horizontal links (spouse connections) - NOT kaweto
    const spouseLinks = links.filter(l => l.type === 'spouse');
    spouseLinks.forEach(link => {
      const source = visibleNodes.get(link.source);
      const target = visibleNodes.get(link.target);
      if (!source || !target) return;

      linkGroup.append('path')
        .attr('d', `M${source.x + nodeWidth/2},${source.y} L${target.x - nodeWidth/2},${target.y}`)
        .attr('fill', 'none')
        .attr('stroke', '#FFB6C1')
        .attr('stroke-width', 2)
        .attr('marker-end', 'url(#arrowhead)');
    });

    // Draw kaweto links - branch from the VERTICAL line below the parent
    const kawetoLinks = links.filter(l => l.type === 'kaweto');
    kawetoLinks.forEach(link => {
      const source = visibleNodes.get(link.source);
      const target = visibleNodes.get(link.target);
      if (!source || !target) return;

      // The kaweto is positioned at branch level (between parent and midY)
      // Draw horizontal line from the source's vertical line (at source.x) to kaweto
      const branchY = target.y; // Kaweto is at branch level
      
      // Draw horizontal line from the vertical line (at source.x) to kaweto
      linkGroup.append('path')
        .attr('d', `M${source.x},${branchY} L${target.x - nodeWidth/2},${branchY}`)
        .attr('fill', 'none')
        .attr('stroke', '#333')
        .attr('stroke-width', 2)
        .attr('marker-end', 'url(#arrowhead)');
    });

    // Draw nodes
    const nodeGroup = g.selectAll('.node')
      .data(nodes, d => d.id)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.x},${d.y})`)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        event.stopPropagation();
        if (expandedNodes.has(d.id)) {
          collapseNode(d.id);
        } else if (d.hasChildren) {
          expandNode(d.id);
        }
      });

    nodeGroup.append('rect')
      .attr('width', nodeWidth)
      .attr('height', nodeHeight)
      .attr('x', -nodeWidth / 2)
      .attr('y', -nodeHeight / 2)
      .attr('rx', 8)
      .attr('ry', 8)
      .attr('fill', d => getNodeColor(d.type, d.depth, d.name))
      .attr('stroke', d => expandedNodes.has(d.id) ? '#4CAF50' : '#333')
      .attr('stroke-width', d => expandedNodes.has(d.id) ? 3 : 2)
      .style('filter', 'drop-shadow(2px 2px 4px rgba(0,0,0,0.2))');

    // Face medallion on nodes that have a photo
    const photoNodes = nodeGroup.filter(d => Boolean(photoById[d.id]));
    photoNodes.append('clipPath')
      .attr('id', d => `nodeclip-${d.id}`)
      .append('circle')
      .attr('cx', -nodeWidth / 2 + photoR + 5)
      .attr('cy', 0)
      .attr('r', photoR);
    photoNodes.append('image')
      .attr('href', d => photoThumb(photoById[d.id]))
      .attr('x', -nodeWidth / 2 + 5)
      .attr('y', -photoR)
      .attr('width', photoR * 2)
      .attr('height', photoR * 2)
      .attr('preserveAspectRatio', 'xMidYMid slice')
      .attr('clip-path', d => `url(#nodeclip-${d.id})`);
    photoNodes.append('circle')
      .attr('cx', -nodeWidth / 2 + photoR + 5)
      .attr('cy', 0)
      .attr('r', photoR)
      .attr('fill', 'none')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);

    // Expand indicator
    nodeGroup.filter(d => d.hasChildren)
      .append('circle')
      .attr('cx', 0)
      .attr('cy', nodeHeight / 2 + 10)
      .attr('r', 8)
      .attr('fill', d => expandedNodes.has(d.id) ? '#4CAF50' : '#2196F3')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    nodeGroup.filter(d => d.hasChildren)
      .append('text')
      .attr('x', 0)
      .attr('y', nodeHeight / 2 + 14)
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .text(d => expandedNodes.has(d.id) ? '−' : '+');

    // Node text
    nodeGroup.each(function(d) {
      const group = d3.select(this);
      const hasPhoto = Boolean(photoById[d.id]);
      const textWidth = hasPhoto ? nodeWidth - photoR * 2 - 16 : nodeWidth - 10;
      const textX = hasPhoto ? photoR + 1 : 0;
      const { lines, lineHeight } = wrapText(d.name, textWidth);
      const totalHeight = lines.length * lineHeight;

      lines.forEach((line, i) => {
        group.append('text')
          .attr('x', textX)
          .attr('y', i * lineHeight - totalHeight / 2 + lineHeight / 2 + 2)
          .attr('text-anchor', 'middle')
          .attr('fill', '#1a1a1a')
          .attr('font-size', isMobile ? '10px' : '12px')
          .attr('font-weight', '600')
          .text(line);
      });
    });

    // Zoom behavior
    const zoom = d3.zoom()
      .scaleExtent([0.3, 3])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
        currentZoomRef.current = event.transform;
      });

    svg.call(zoom);

    // Pan to clicked node or center on root
    const targetNode = lastClickedNodeId ? visibleNodes.get(lastClickedNodeId) : rootNode;
    
    if (targetNode) {
      const scale = currentZoomRef.current?.k || (isMobile ? 0.8 : 1.0);
      // Keep clicked node in upper portion of screen
      const targetX = width / 2 - targetNode.x * scale + margin.left;
      const targetY = height / 4 - targetNode.y * scale + margin.top;
      
      const newTransform = d3.zoomIdentity.translate(targetX, targetY).scale(scale);
      svg.transition().duration(300).call(zoom.transform, newTransform);
      currentZoomRef.current = newTransform;
    }

  }, [dimensions, isReady, visibleNodes, visibleLinks, expandedNodes, isMobile, expandNode, collapseNode, lastClickedNodeId, personMap]);

  // Print handler
  const handlePrint = () => {
    const svgElement = svgRef.current;
    if (!svgElement) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to print');
      return;
    }
    
    const svgClone = svgElement.cloneNode(true);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Mwendwa Kitavi Family Tree</title>
          <style>
            @page { size: landscape; margin: 0.5cm; }
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .header { text-align: center; margin-bottom: 10px; }
            .header h1 { margin: 0; font-size: 24px; color: #333; }
            svg { width: 100%; height: auto; max-height: 90vh; }
            @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="header"><h1>Mwendwa Kitavi Family Tree</h1></div>
          ${svgClone.outerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const nodeCount = visibleNodes.size;
  const linkCount = visibleLinks.length;

  return (
    <Box position="relative" style={{ width: '100%', height: isMobile ? '70vh' : '80vh', backgroundColor: '#f8f9fa' }} className="flowchart-container">
      {/* Legend */}
      <Box
        style={{
          position: 'absolute',
          top: '10px',
          left: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          zIndex: 2000,
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '6px 10px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          flexWrap: 'wrap',
          maxWidth: '70%'
        }}
      >
        <Typography style={{ fontSize: '11px', fontWeight: 600, color: '#666', marginRight: '4px' }}>Legend:</Typography>
        <Chip label="Ancestors" size="small" style={{ backgroundColor: '#A0A0A0', color: '#fff', height: '20px', fontSize: '10px' }} />
        <Chip label="Mwendwa" size="small" style={{ backgroundColor: '#00FFFF', color: '#000', height: '20px', fontSize: '10px' }} />
        <Chip label="Spouse" size="small" style={{ backgroundColor: '#FFB6C1', color: '#000', height: '20px', fontSize: '10px' }} />
        <Chip label="Children" size="small" style={{ backgroundColor: '#98FB98', color: '#000', height: '20px', fontSize: '10px' }} />
        <Chip label="G.Children" size="small" style={{ backgroundColor: '#FFFACD', color: '#000', height: '20px', fontSize: '10px' }} />
      </Box>

      {/* Controls */}
      <Box
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          display: 'flex',
          gap: '8px',
          zIndex: 2000,
        }}
      >
        {selectedPerson && (
          <IconButton
            onClick={onBack}
            style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
          >
            <CloseIcon style={{ color: '#333' }} />
          </IconButton>
        )}
        <IconButton
          onClick={resetTree}
          style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <RefreshIcon style={{ color: '#333' }} />
        </IconButton>
        <IconButton
          onClick={handlePrint}
          style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
        >
          <PrintIcon style={{ color: '#333' }} />
        </IconButton>
      </Box>

      {/* Stats */}
      <Box
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '6px 12px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 2000,
        }}
      >
        <Typography style={{ fontSize: '11px', color: '#666' }}>
          Showing {nodeCount} people, {linkCount} relationships
        </Typography>
      </Box>

      {/* Instructions */}
      <Box
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(255,255,255,0.95)',
          padding: '6px 12px',
          borderRadius: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          zIndex: 2000,
        }}
      >
        <Typography style={{ fontSize: '11px', color: '#666' }}>
          Click + to expand • Click − to collapse • Scroll to zoom • Drag to pan
        </Typography>
      </Box>

      <div ref={containerRef} style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        {!isReady ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Typography>Loading flowchart...</Typography>
          </Box>
        ) : (
          <svg ref={svgRef}></svg>
        )}
      </div>
    </Box>
  );
};

export default FlowChartView;